import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { modalHeaderBtnSx } from '../chatMessageStyles';
import AudioCardBuilder from './AudioCardBuilder';
import AudioCardPlayer from './AudioCardPlayer';
import { createCard, deleteCard, listCards, orderCards } from './api';
import { CardIcon, WaveIcon, scheduleLabel } from './cardModel';
import './audio.css';

/** A round button with a label — the dashboard's audio circle (App.js), plus
 *  the schedule under the name, the "new" dot and the edit-mode controls. */
function AudioCircle({ icon, label, sub, badge = 'audio', dashed, isNew, newLabel, fresh, onClick, onRemove, dragProps }) {
  return (
    <Box className={fresh ? 'ac-circle-fresh card-enter' : 'card-enter'} {...dragProps}
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', transition: 'transform 0.2s ease', '&:hover': { transform: 'translateY(-2px)' } }}>
        <IconButton onClick={onClick} aria-label={label}
          sx={{
            width: { xs: 64, md: 72 }, height: { xs: 64, md: 72 }, borderRadius: '50%',
            bgcolor: dashed ? 'transparent' : 'background.paper',
            border: dashed ? '1.5px dashed rgba(25, 208, 239, 0.6)' : 1,
            borderColor: dashed ? undefined : (badge === 'edit' ? 'rgba(25, 208, 239, 0.6)' : 'divider'),
            color: 'primary.main', '& .MuiSvgIcon-root': { fontSize: 28 },
            transition: 'border-color 0.2s ease, background-color 0.2s ease',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}>
          {icon}
        </IconButton>
        <Box sx={{
          position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
          bgcolor: badge === 'edit' ? '#fff' : 'primary.main', color: badge === 'edit' ? '#0a0a2e' : 'primary.contrastText',
          display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 1, pointerEvents: 'none',
          '& .MuiSvgIcon-root': { fontSize: 11 },
        }}>
          {badge === 'edit' ? <EditIcon /> : badge === 'voice' ? <WaveIcon size={11} /> : <VolumeUpRoundedIcon />}
        </Box>
        {isNew && badge !== 'edit' && (
          <Box title={newLabel} sx={{ position: 'absolute', top: 4, left: 4, width: 10, height: 10, borderRadius: '50%',
            bgcolor: 'primary.main', boxShadow: '0 0 0 3px rgba(25, 208, 239, 0.25)', pointerEvents: 'none' }} />
        )}
        {onRemove && (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label={`${label} ✕`}
            sx={{ position: 'absolute', top: -6, left: -6, width: 22, height: 22, bgcolor: '#2a2f5e', color: 'text.primary',
              border: '1px solid rgba(255,255,255,0.25)', '&:hover': { bgcolor: 'error.main', borderColor: 'error.main' },
              '& .MuiSvgIcon-root': { fontSize: 13 } }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', lineHeight: 1.2, maxWidth: 96, overflowWrap: 'anywhere' }}>
        {label}
        {sub && <Box component="span" sx={{ display: 'block', fontSize: '10.5px', opacity: 0.6, mt: 0.25 }}>{sub}</Box>}
      </Typography>
    </Box>
  );
}

/**
 * "Deine Audios" on the home screen: the user's own audio cards (no fixed
 * cards any more). Tap = play; "Bearbeiten" = edit mode (✕ deletes, tap opens
 * the form, drag to reorder); "Neues Audio" = Bro, by speech.
 */
export default function AudioCards({ language, spaced }) {
  const { t } = useTranslation();
  const [data, setData] = useState({ cards: [], catalog: null, sources: {} });
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [playing, setPlaying] = useState(null);       // card
  const [building, setBuilding] = useState(null);     // { card|null, voice }
  const [banner, setBanner] = useState('');
  const [freshId, setFreshId] = useState(null);
  const [undo, setUndo] = useState(null);             // { card, position }
  const [dragFrom, setDragFrom] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await listCards(language);
      setData({ cards: res.cards || [], catalog: res.catalog, sources: res.sources || {} });
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoaded(true);
    }
  }, [language]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!banner) return undefined;
    const id = setTimeout(() => setBanner(''), 7000);
    return () => clearTimeout(id);
  }, [banner]);

  const { cards, catalog, sources } = data;
  const noData = loaded && !error && Object.keys(sources).length === 0;

  const saved = (card, created) => {
    setBuilding(null);
    setPlaying(null);
    setFreshId(card.id);
    setBanner(created ? t('audio_saved_new', { name: card.name }) : t('audio_saved_changed', { name: card.name }));
    load();
  };

  const remove = async (card) => {
    const position = cards.findIndex((c) => c.id === card.id);
    setPlaying(null);
    setData((d) => ({ ...d, cards: d.cards.filter((c) => c.id !== card.id) }));
    try {
      await deleteCard(card.id);
      setUndo({ card, position });
    } catch (e) {
      setError(e.message);
      load();
    }
  };

  const restore = async () => {
    const { card, position } = undo;
    setUndo(null);
    try {
      const res = await createCard(card, { position, restore: true });
      setFreshId(res.card.id);
    } catch (e) {
      setError(e.message);
    }
    load();
  };

  const drop = async (to) => {
    if (dragFrom === null || dragFrom === to) { setDragFrom(null); return; }
    const next = [...cards];
    next.splice(to, 0, next.splice(dragFrom, 1)[0]);
    setDragFrom(null);
    setData((d) => ({ ...d, cards: next }));
    try { await orderCards(next.map((c) => c.id)); } catch (e) { load(); }
  };

  if (!loaded) return <Box sx={{ minHeight: 120, mb: spaced ? 4 : 0 }} />;

  return (
    <Box sx={{ mb: spaced ? 4 : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 32, mb: 1.5 }}>
        <Typography sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '11px', fontWeight: 600 }}>
          {t('audio_section')}
        </Typography>
        {cards.length > 0 && (
          <Button size="small" onClick={() => setEditMode(!editMode)}
            sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.25, fontSize: '13px', fontWeight: 600,
              ...(editMode && { color: 'primary.main', borderColor: 'primary.main' }) }}>
            {editMode ? t('audio_done') : t('audio_edit')}
          </Button>
        )}
      </Box>

      {banner && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, mb: 2, borderRadius: '10px',
          bgcolor: 'rgba(25, 208, 239, 0.08)', border: '1px solid', borderColor: 'rgba(25, 208, 239, 0.3)', color: 'primary.main' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{banner}</Typography>
        </Box>
      )}
      {error && <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{error}</Typography>}
      {noData && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>{t('audio_no_data')}</Typography>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2, justifyItems: 'center' }}>
        {cards.map((card, i) => (
          <AudioCircle
            key={card.id}
            icon={<CardIcon name={card.icon} />}
            label={card.name}
            sub={scheduleLabel(catalog, card.schedule)}
            badge={editMode ? 'edit' : 'audio'}
            isNew={card.has_new}
            newLabel={t('audio_new_dot')}
            fresh={card.id === freshId}
            onClick={() => (editMode ? setBuilding({ card, voice: false }) : setPlaying(card))}
            onRemove={editMode ? () => remove(card) : undefined}
            dragProps={editMode ? {
              draggable: true,
              onDragStart: () => setDragFrom(i),
              onDragOver: (e) => e.preventDefault(),
              onDrop: (e) => { e.preventDefault(); drop(i); },
              style: { animationDelay: `${i * 80}ms`, cursor: 'grab' },
            } : { style: { animationDelay: `${i * 80}ms` } }}
          />
        ))}
        {!noData && (
          <AudioCircle
            icon={<AddIcon />}
            label={t('audio_new')}
            sub={t('audio_new_sub')}
            badge="voice"
            dashed
            onClick={() => setBuilding({ card: null, voice: true })}
            dragProps={{ style: { animationDelay: `${cards.length * 80}ms` } }}
          />
        )}
      </Box>
      {cards.length > 0 && !editMode && (
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', opacity: 0.7, mt: 1.5 }}>
          {t('audio_hint')}
        </Typography>
      )}
      {editMode && (
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', opacity: 0.7, mt: 1.5 }}>
          {t('audio_hint_edit')}
        </Typography>
      )}

      {playing && (
        <AudioCardPlayer
          card={playing}
          catalog={catalog}
          language={language}
          onClose={() => { setPlaying(null); load(); }}
          onEditVoice={() => { setBuilding({ card: playing, voice: true }); setPlaying(null); }}
          onEditForm={() => { setBuilding({ card: playing, voice: false }); setPlaying(null); }}
          onDelete={() => remove(playing)}
        />
      )}
      {building && (
        <AudioCardBuilder
          card={building.card}
          catalog={catalog}
          sources={sources}
          language={language}
          startWithVoice={building.voice}
          onClose={() => setBuilding(null)}
          onSaved={saved}
        />
      )}
      <Snackbar
        open={!!undo}
        autoHideDuration={6000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setUndo(null); }}
        message={undo ? t('audio_deleted', { name: undo.card.name }) : ''}
        action={<Button size="small" onClick={restore} sx={{ color: 'primary.main', fontWeight: 600 }}>{t('audio_undo')}</Button>}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
